/**
 * ruleUtilsKeys.ts
 */

import { ScaleOutputValueType } from "@/utilities/scale/rules/ruleConstants";
import type { ScaleD3Config, ScaleOutput, ScaleRule } from "@/utilities/scale/rules/ruleInterfaces";

// ###############################
// # Unique UI Keys
// ###############################

/**
 * Generates a unique React key for a scale rule based on its index and all values.
 *
 * @param index - The index of the rule or other identifier
 * @param rule - The complete rule object
 * @returns A string key that can be used as a React key prop
 */
export function getScaleRuleReactKey(index: number | string, rule: ScaleRule): string {
  const ruleKey = JSON.stringify(rule); // TODO(noske): Make this shorter or use a hash function for better performance.
  return `${index}-${ruleKey}`;
}

/**
 * Generates a unique React key for a scale rule based on its index and output values.
 * This ensures proper re-rendering when rule output changes (e.g. on preset load).
 *
 * @param index - The index of the rule or other identifier
 * @param output - The output value object containing outputValues array
 * @returns A string key that can be used as a React key prop
 */
export function getScaleOutputReactKey(index: number | string, output: ScaleOutput): string {
  const outputKeys = (output.outputValues || []).join(",");
  if (output.outputType === ScaleOutputValueType.D3Scale && output.scaleD3Config) {
    const d3Key = getScaleD3ConfigReactKey(output.scaleD3Config);
    return `${index}-${outputKeys}-${d3Key}`;
  }
  return `${index}-${outputKeys}`;
}

/**
 * Generates a unique React key for a ScaleD3Config object, omitting the finalD3Scale property.
 *
 * @param config - The ScaleD3Config object
 * @returns A string key that can be used as a React key prop
 */
export function getScaleD3ConfigReactKey(config: ScaleD3Config): string {
  return (
    config.d3ScaleType +
    (config.rampReverse ? "-reverse" : "") +
    (config.rampSteps ? `-steps${config.rampSteps}` : "")
  );
}
